import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AtSignIcon, LockIcon, UserIcon, AppleIcon, LinkedinIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
interface LoginProps {
  onLogin: (userType: 'mentor' | 'mentee', skipProfileSetup?: boolean) => void;
}
const Login: React.FC<LoginProps> = ({
  onLogin
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [userTypeSelection, setUserTypeSelection] = useState<'mentor' | 'mentee'>('mentee');
  const [showPassword, setShowPassword] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Test account checks
    if (email === 'tm@tm.co' && password === 'tmtmtm' || email === 'tester@test.com' && password === 'test!234') {
      const userData = {
        email,
        userType: 'mentee',
        profileComplete: true
      };
      localStorage.setItem('userData', JSON.stringify(userData));
      onLogin('mentee', true); // Skip profile setup for test user
      return;
    }
    if (email === 't@tm.co' && password === 'tmtmtm' || email === 'trenty@test.com' && password === 'test!234') {
      const userData = {
        email,
        userType: 'mentor',
        profileComplete: true
      };
      localStorage.setItem('userData', JSON.stringify(userData));
      onLogin('mentor', true); // Skip profile setup for test user
      return;
    }
    // For all other users
    if (email && password.length >= 6) {
      const userData = {
        email,
        userType: userTypeSelection,
        profileComplete: false
      };
      localStorage.setItem('userData', JSON.stringify(userData));
      onLogin(userTypeSelection);
    }
  };
  return <div className="min-h-screen flex flex-col bg-gradient-to-b from-secondary to-dark p-6" data-id="element-231">
      <div className="flex justify-center mb-10 mt-8" data-id="element-232">
        <div className="flex flex-col items-center" data-id="element-233">
          <div className="w-16 h-16 bg-dark-800 rounded-full flex items-center justify-center mb-3" data-id="element-234">
            <div className="text-primary font-bold text-3xl" data-id="element-235">PM</div>
          </div>
          <h1 className="text-white text-2xl font-bold" data-id="element-236">
            PrideMentor <span className="text-primary" data-id="element-237">✦</span>
          </h1>
          <p className="text-gray-300 text-sm" data-id="element-238">Connect. Grow. Thrive.</p>
        </div>
      </div>
      <div className="bg-dark-800 rounded-xl p-6 shadow-card border border-dark-600" data-id="element-239">
        <h2 className="text-xl font-bold text-center mb-6 text-white" data-id="element-240">
          Welcome Back
        </h2>
        <div className="flex justify-center space-x-4 mb-6" data-id="element-241">
          <button className={`px-4 py-2 rounded-full ${userTypeSelection === 'mentee' ? 'bg-primary text-dark font-bold' : 'bg-dark-700 text-gray-300'}`} onClick={() => setUserTypeSelection('mentee')} data-id="element-242">
            I'm a Mentee
          </button>
          <button className={`px-4 py-2 rounded-full ${userTypeSelection === 'mentor' ? 'bg-primary text-dark font-bold' : 'bg-dark-700 text-gray-300'}`} onClick={() => setUserTypeSelection('mentor')} data-id="element-243">
            I'm a Mentor
          </button>
        </div>
        <form onSubmit={handleSubmit} data-id="element-244">
          <div className="mb-4" data-id="element-245">
            <label className="block text-gray-300 text-sm font-medium mb-1" data-id="element-246">
              Email
            </label>
            <div className="relative" data-id="element-247">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-248">
                <AtSignIcon size={18} className="text-gray-400" data-id="element-249" />
              </div>
              <input type="email" className="w-full pl-10 pr-3 py-2 bg-dark-700 border border-dark-500 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary" placeholder="Enter your email" value={email} onChange={e => setEmail(e.target.value)} required data-id="element-250" />
            </div>
          </div>
          <div className="mb-6" data-id="element-251">
            <label className="block text-gray-300 text-sm font-medium mb-1" data-id="element-252">
              Password
            </label>
            <div className="relative" data-id="element-253">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-254">
                <LockIcon size={18} className="text-gray-400" data-id="element-255" />
              </div>
              <input type={showPassword ? 'text' : 'password'} className="w-full pl-10 pr-10 py-2 bg-dark-700 border border-dark-500 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} required data-id="element-256" />
              <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowPassword(!showPassword)} data-id="element-257">
                {showPassword ? <EyeOffIcon size={18} className="text-gray-400" data-id="element-258" /> : <EyeIcon size={18} className="text-gray-400" data-id="element-259" />}
              </button>
            </div>
            <div className="text-right mt-1" data-id="element-260">
              <a href="#" className="text-sm text-primary hover:underline" data-id="element-261">
                Forgot password?
              </a>
            </div>
          </div>
          <button type="submit" className="w-full bg-primary text-dark py-2 rounded-lg font-bold hover:bg-yellow-dark transition-colors" data-id="element-262">
            Sign In
          </button>
        </form>
        <div className="my-6 flex items-center" data-id="element-263">
          <div className="flex-1 border-t border-dark-600" data-id="element-264"></div>
          <span className="px-3 text-gray-400 text-sm" data-id="element-265">or continue with</span>
          <div className="flex-1 border-t border-dark-600" data-id="element-266"></div>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-6" data-id="element-267">
          <button className="flex justify-center items-center py-2 border border-dark-500 bg-dark-700 rounded-lg hover:bg-dark-600" data-id="element-268">
            <UserIcon size={18} className="text-primary" data-id="element-269" />
          </button>
          <button className="flex justify-center items-center py-2 border border-dark-500 bg-dark-700 rounded-lg hover:bg-dark-600" data-id="element-270">
            <AppleIcon size={18} className="text-white" data-id="element-271" />
          </button>
          <button className="flex justify-center items-center py-2 border border-dark-500 bg-dark-700 rounded-lg hover:bg-dark-600" data-id="element-272">
            <LinkedinIcon size={18} className="text-primary" data-id="element-273" />
          </button>
        </div>
        <p className="text-center text-gray-300 text-sm" data-id="element-274">
          Don't have an account?{' '}
          <Link to="/signup" className="text-primary font-medium hover:underline" data-id="element-275">
            Sign up
          </Link>
        </p>
        <div className="mt-6 text-center text-xs text-gray-400" data-id="element-276">
          <p data-id="element-277">By continuing, you agree to our</p>
          <div className="mt-1" data-id="element-278">
            <a href="#" className="text-primary hover:underline" data-id="element-279">
              Terms of Service
            </a>
            {' & '}
            <a href="#" className="text-primary hover:underline" data-id="element-280">
              Privacy Policy
            </a>
          </div>
        </div>
      </div>
    </div>;
};
export default Login;